import React from 'react';
import { HelpCircle, X, Sun, Focus, Layers, Check, ZoomIn } from 'lucide-react';

interface ScannerHelpOverlayProps {
  onCloseHelp: () => void;
}

export const ScannerHelpOverlay: React.FC<ScannerHelpOverlayProps> = ({ onCloseHelp }) => {
  return (
    <div className="absolute inset-0 bg-slate-950/96 backdrop-blur-md flex flex-col p-3.5 sm:p-4 text-white animate-in fade-in zoom-in-98 duration-150 z-50 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-800/80 pb-2.5 mb-2.5 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center border border-indigo-500/30">
            <HelpCircle className="w-3.5 h-3.5" />
          </div>
          <div>
            <h4 className="font-bold text-xs sm:text-sm text-white">Petunjuk Pemindaian Barcode & QR</h4>
            <p className="text-[10px] text-slate-400">Tips agar kode kecil atau redup terdeteksi instan</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onCloseHelp}
          className="w-7 h-7 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 flex items-center justify-center transition cursor-pointer"
          title="Tutup Petunjuk (Esc)"
          aria-label="Tutup Petunjuk"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Grid of Tips */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] my-auto">
        {/* Tip 1: Zoom & Barcode Kecil */}
        <div className="flex items-start gap-2.5 bg-slate-900/80 p-2.5 rounded-xl border border-indigo-500/40 shadow-2xs">
          <div className="w-5 h-5 rounded-md bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 flex items-center justify-center shrink-0 font-bold text-[10px]">
            <ZoomIn className="w-3 h-3" />
          </div>
          <div>
            <p className="font-semibold text-indigo-300 mb-0.5">Kode Berukuran Kecil?</p>
            <p className="text-slate-300 leading-snug text-[10.5px]">
              Gunakan tombol <b>2x Makro</b> atau <b>3x Mikro</b> di pojok kanan atas untuk memperbesar sensor kamera tanpa blur.
            </p>
          </div>
        </div>

        {/* Tip 2 */}
        <div className="flex items-start gap-2.5 bg-slate-900/80 p-2.5 rounded-xl border border-slate-800/90 shadow-2xs">
          <div className="w-5 h-5 rounded-md bg-amber-500/20 text-amber-400 border border-amber-500/30 flex items-center justify-center shrink-0 font-bold text-[10px]">
            <Sun className="w-3 h-3" />
          </div>
          <div>
            <p className="font-semibold text-slate-200 mb-0.5">Cahaya & Senter</p>
            <p className="text-slate-400 leading-snug text-[10.5px]">
              Hindari bayangan/pantulan silau. Gunakan ikon senter jika ruangan gelap.
            </p>
          </div>
        </div>

        {/* Tip 3 */}
        <div className="flex items-start gap-2.5 bg-slate-900/80 p-2.5 rounded-xl border border-slate-800/90 shadow-2xs">
          <div className="w-5 h-5 rounded-md bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center shrink-0 font-bold text-[10px]">
            <Focus className="w-3 h-3" />
          </div>
          <div>
            <p className="font-semibold text-slate-200 mb-0.5">Jarak Fokus Stabil</p>
            <p className="text-slate-400 leading-snug text-[10.5px]">
              Posisikan barcode di dalam kotak tengah agar algoritma pemindai makro mengunci garis secara tajam.
            </p>
          </div>
        </div>

        {/* Tip 4 */}
        <div className="flex items-start gap-2.5 bg-slate-900/80 p-2.5 rounded-xl border border-slate-800/90 shadow-2xs">
          <div className="w-5 h-5 rounded-md bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 flex items-center justify-center shrink-0 font-bold text-[10px]">
            <Layers className="w-3 h-3" />
          </div>
          <div>
            <p className="font-semibold text-slate-200 mb-0.5">Pilihan Profil Target</p>
            <p className="text-slate-400 leading-snug text-[10.5px]">
              Ganti mode reticle ke <b>Barcode 1D</b> untuk kode garis atau <b>QR 2D</b> untuk stiker kotak.
            </p>
          </div>
        </div>
      </div>

      {/* Prominent Bottom Dismiss Button */}
      <div className="pt-2.5 mt-2 border-t border-slate-800/80 shrink-0">
        <button
          type="button"
          onClick={onCloseHelp}
          className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 active:bg-indigo-700 text-white font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 transition cursor-pointer shadow-md"
        >
          <Check className="w-3.5 h-3.5" />
          <span>Mengerti, Kembali ke Kamera</span>
        </button>
      </div>
    </div>
  );
};
